<template>
  <div class="stock-view-container">
    <!-- Header -->
    <div class="header">
      <h1><i class="fas fa-boxes"></i> Gestión de Stock</h1>
      <div class="header-actions">
        <button class="btn btn-primary" @click="fetchStock">
          <i class="fas fa-sync-alt"></i> Actualizar
        </button>
      </div>
    </div>

    <!-- Buscador -->
    <div class="filters">
      <div class="search-box">
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Buscar producto..."
          class="search-input"
        />
        <i class="fas fa-search search-icon"></i>
      </div>
    </div>

    <!-- Contenido -->
    <div class="stock-content">
      <!-- Estado de carga -->
      <div v-if="loading" class="loading">
        <i class="fas fa-spinner fa-spin"></i> Cargando stock...
      </div>

      <!-- Error -->
      <div v-else-if="error" class="error-message">
        <i class="fas fa-exclamation-triangle"></i>
        <p>Error al cargar el stock: {{ error }}</p>
        <button class="btn btn-secondary" @click="fetchStock">Reintentar</button>
      </div>

      <!-- Categorías -->
      <div v-else class="stock-sections">
        <div
          class="category-section"
          v-for="category in filteredCategories"
          :key="category.nombre"
        >
          <div class="category-header">
            <h2>{{ category.nombre }}</h2>
            <span class="product-count">
              {{ category.productos.length }} productos
            </span>
          </div>

          <div class="products-grid">
            <div
              v-for="producto in category.productos"
              :key="producto.id"
              class="product-card"
            >
              <div class="product-info">
                <h3 class="product-name">{{ producto.producto_nombre }}</h3>
                <div class="stock-info">
                  <span class="stock-label">Stock disponible:</span>
                  <span
                    class="stock-quantity"
                    :class="{
                      'low-stock': producto.cantidad <= producto.minimo,
                      'out-of-stock': producto.cantidad <= 0
                    }"
                  >
                    {{ producto.cantidad }} unidades
                  </span>
                </div>
              </div>

              <button
                class="btn btn-outline-primary btn-sm"
                @click="openEditForm(producto)"
              >
                <i class="fas fa-edit"></i> Editar
              </button>
            </div>
          </div>
        </div>

        <!-- Sin resultados -->
        <div v-if="filteredCategories.length === 0" class="no-results">
          <i class="fas fa-search"></i>
          <p>No se encontraron productos que coincidan con la búsqueda</p>
        </div>
      </div>
    </div>

    <!-- Modal de edición -->
    <StockFormComponent
      v-if="showForm"
      :show="showForm"
      :producto="selectedProduct"
      @close="showForm = false"
      @save="saveStock"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import stockAPI from '@/api/stockAPI'
import StockFormComponent from '@/components/stock/StockFormComponent.vue'

const loading = ref(false)
const error = ref(null)
const searchQuery = ref('')
const stockData = ref([])
const selectedProduct = ref(null)
const showForm = ref(false)

// Categorías (coinciden con el backend)
const categories = ref([
  'Grupo Modems',
  'Grupo QPay',
  'Grupo Ingénico',
  'Grupo Sueltos'
])

// 📦 Obtener stock desde API
const fetchStock = async () => {
  loading.value = true
  error.value = null

  try {
    const response = await stockAPI.getStock()
    // Si tu backend devuelve lista plana (sin results)
    stockData.value = Array.isArray(response) ? response : response.results
  } catch (err) {
    error.value = err.response?.data?.error || 'Error al cargar el stock'
    console.error('Error fetching stock:', err)
  } finally {
    loading.value = false
  }
}

// 🧮 Filtrar y agrupar por categoría
const filteredCategories = computed(() => {
  if (!stockData.value || !Array.isArray(stockData.value)) return []

  const filtered = []
  const categorized = {
    'Grupo Modems': [],
    'Grupo QPay': [],
    'Grupo Ingénico': [],
    'Grupo Sueltos': []
  }

  stockData.value.forEach((p) => {
    if (!p) return
    const matchesSearch = p.producto_nombre
      ?.toLowerCase()
      .includes(searchQuery.value.toLowerCase())

    if (!matchesSearch) return

    if (p.producto_categoria === 'Modem') categorized['Grupo Modems'].push(p)
    else if (p.producto_categoria === 'QPay') categorized['Grupo QPay'].push(p)
    else if (p.producto_categoria === 'Ingenico') categorized['Grupo Ingénico'].push(p)
    else if (p.producto_categoria === 'Suelto') categorized['Grupo Sueltos'].push(p)
    else {
      if (!categorized['Otros']) categorized['Otros'] = []
      categorized['Otros'].push(p)
    }
  })

  for (const [nombre, productos] of Object.entries(categorized)) {
    if (productos.length > 0) filtered.push({ nombre, productos })
  }

  return filtered
})

// ✏️ Abrir formulario de edición
const openEditForm = (producto) => {
  if (!producto) return
  selectedProduct.value = producto
  showForm.value = true
}

// 💾 Guardar cambios (PATCH)
const saveStock = async (data) => {
  try {
    await stockAPI.updateStock(data.id, {
      cantidad: data.cantidad,
      minimo: data.minimo
    })
    await fetchStock()
  } catch (err) {
    console.error('Error al actualizar stock:', err)
  } finally {
    showForm.value = false
  }
}

onMounted(fetchStock)
</script>

<style scoped>
.stock-view-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.filters {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
}
.search-box {
  position: relative;
  flex: 1;
}
.search-input {
  width: 100%;
  padding: 10px 15px 10px 40px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
}
.search-icon {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
}
.stock-content {
  margin-top: 20px;
}
.loading,
.error-message {
  text-align: center;
  padding: 40px;
  color: #666;
}
.error-message {
  color: #e74c3c;
}
.category-section {
  margin-bottom: 30px;
}
.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #eee;
}
.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 15px;
}
.product-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s;
}
.product-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
.product-info {
  flex: 1;
}
.product-name {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 500;
}
.stock-info {
  display: flex;
  flex-direction: column;
}
.stock-label {
  font-size: 12px;
  color: #7f8c8d;
  margin-bottom: 2px;
}
.stock-quantity {
  font-weight: 600;
  font-size: 18px;
}
.low-stock {
  color: #f39c12;
}
.out-of-stock {
  color: #e74c3c;
}
.no-results {
  text-align: center;
  padding: 40px;
  color: #7f8c8d;
}
.no-results i {
  font-size: 48px;
  margin-bottom: 15px;
  display: block;
}
</style>
